import { Component, ViewChild, OnInit } from '@angular/core';
import { SearchCompaniesComponent } from '../companies/search-companies/search-companies.component';
import { CompanyUniverseSearch } from '../../models/enums/company-universe-search';
import { AppContextService } from '../../services/app-context.service';
import { User } from '../../models/User/user';
import { CustomTabItem, TabAction } from '../../shared-components/custom-tabs/custom-tabs.component';
import { TranslateService } from '../../services/translate/translate.service';
import { Router } from '@angular/router';
import { MenuItem } from '../../shared-components/custom-menu/custom-menu.component';

@Component({
	selector: 'app-companies-lists',
	templateUrl: './companies-lists.component.html',
	styleUrls: ['./companies-lists.component.css'],
	standalone: false,
})
export class CompaniesListsComponent implements OnInit {
	public loggedUser: User | null = new User();
	public isPerformingRequest: boolean = false;

	constructor(
		private appContext: AppContextService,
		private translateService: TranslateService,
		private router: Router
	) {}

	public activeTabId: string = 'myCompanies';
	public tabs: CustomTabItem[] = [];
	public tabActions: TabAction[] = [];

	private buildTabs(): CustomTabItem[] {
		return [
			{
				id: 'myCompanies',
				label: this.translateService.translate('search-companies.myCompanies', 'client'),
				condition: !this.loggedUser?.IsSystemAdmin && !this.loggedUser?.IsSupportOperator && !this.loggedUser?.IsBackOfficeOperator,
			},
			{
				id: 'myConnections',
				label: this.translateService.translate('search-companies.myConnections', 'client'),
				condition: !this.loggedUser?.IsSystemAdmin && !this.loggedUser?.IsSupportOperator && !this.loggedUser?.IsBackOfficeOperator,
			},
			{
				id: 'myNetwork',
				label: this.translateService.translate('search-companies.network', 'client'),
			},
		];
	}

	private buildTabActions(): TabAction[] {
		return [
			{
				type: 'menu',
				text: this.translateService.translate('search-companies.newContract'),
				menuItems: this.newContractMenuItems,
				condition: this.canSeeNewContract(),
			},
			{
				type: 'menu',
				text: this.translateService.translate('search-companies.inviteInexistingCompanyAsSupplier'),
				menuItems: this.inviteSupplierMenuItems,
				condition: this.canSeeInviteSupplier(),
			},
			{
				type: 'button',
				text: this.translateService.translate('search-companies.gotoContractAddBuyerRequest'),
				action: () => this.gotoAddBuyerRequest(),
				condition: this.canSeeAddBuyerRequest(),
			},
			{
				type: 'button',
				text: this.translateService.translate('search-companies.pendingInvites'),
				action: () => this.gotoInvites(),
				condition: this.canSeeInvites(),
			},
		];
	}

	public onTabSelect(tabId: string): void {
		this.activeTabId = tabId;
		this.tabActions = this.buildTabActions();
	}

	ngOnInit(): void {
		this.isPerformingRequest = true;
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (!!this.loggedUser) {
				this.activeTabId =
					this.loggedUser.IsSystemAdmin || this.loggedUser?.IsSupportOperator || this.loggedUser?.IsBackOfficeOperator
						? 'myNetwork'
						: 'myCompanies';
				this.tabs = this.buildTabs();
				this.tabActions = this.buildTabActions();
			}
			this.isPerformingRequest = false;
		});
	}

	public myCompanyUniverseSearch: CompanyUniverseSearch = CompanyUniverseSearch.MyCompanies;
	public myConnectionsUniverseSearch: CompanyUniverseSearch = CompanyUniverseSearch.MyConnections;
	public networkUniverseSearch: CompanyUniverseSearch = CompanyUniverseSearch.Network;

	@ViewChild('searchMyCompaniesComponent', { static: true }) searchMyCompaniesComponent: SearchCompaniesComponent;
	@ViewChild('searchConnectedCompaniesComponent', { static: true }) searchConnectedCompaniesComponent: SearchCompaniesComponent;
	@ViewChild('searchNetworkCompaniesComponent', { static: true }) searchNetworkCompaniesComponent: SearchCompaniesComponent;

	public performSearchOnMyCompanies(page: number) {
		this.searchMyCompaniesComponent.performSearch(page);
	}

	public performSearchOnMyConnections(page: number) {
		this.searchConnectedCompaniesComponent.performSearch(page);
	}

	public performSearchOnNetwork(page: number) {
		this.searchNetworkCompaniesComponent.performSearch(page);
	}

	public inviteSupplierMenuItems: MenuItem[] = [
		{
			text: 'search-companies.inviteNewSupplier',
			action: () => this.gotoInviteSupplier(),
		},
		{
			text: 'search-companies.massiveSupplierUpload',
			action: () => this.gotoMassiveInviteSupplier(),
		},
	];

	public gotoInviteSupplier(): void {
		this.router.navigate(['/private/invitation/confirming/company/new/supplier/step1']);
	}

	public gotoMassiveInviteSupplier(): void {
		this.router.navigate(['/private/files-upload/upload/suppliers/step1']);
	}

	public newContractMenuItems: MenuItem[] = [
		{
			text: 'search-companies.newConfirmingContract',
			action: () => this.gotoNewConfirmingContract(),
		},
		{
			text: 'search-companies.newFactoringContract',
			action: () => this.gotoNewFactoringContract(),
		},
	];

	public gotoNewConfirmingContract(): void {
		this.router.navigate(['/private/invitation/confirming/company/new/buyer/step1']);
	}

	public gotoNewFactoringContract(): void {
		this.router.navigate(['/private/invitation/factoring/company/new/supplier/step1']);
	}

	public gotoAddBuyerRequest(): void {
		this.router.navigate(['/private/contracts/factoring/add/buyer/step1']);
	}

	public gotoInvites(): void {
		this.router.navigate(['/private/invitation/company/search']);
	}

	canSeeNewContract(): boolean {
		return this.userCanRegisterNewContracts() && this.activeTabCompanyUniverseSearch !== CompanyUniverseSearch.MyCompanies;
	}

	canSeeInviteSupplier(): boolean {
		return this.userCanInviteCompaniesAsSupplier() && this.activeTabCompanyUniverseSearch !== CompanyUniverseSearch.MyCompanies;
	}

	canSeeAddBuyerRequest(): boolean {
		return this.userCanRequestBuyerAssociation() && this.activeTabCompanyUniverseSearch !== CompanyUniverseSearch.MyCompanies;
	}

	canSeeInvites(): boolean {
		return (
			(this.userCanInviteCompaniesAsSupplier() || this.userCanBeInvitedAsSupplier() || this.userCanRegisterNewContracts()) &&
			this.activeTabCompanyUniverseSearch !== CompanyUniverseSearch.MyCompanies
		);
	}

	private userCanRegisterNewContracts(): boolean {
		if (!this.loggedUser) return false;
		if (this.loggedUser.IsSystemAdmin) return true;
		if (this.loggedUser.IsSupportOperator) return true;
		return !!(this.loggedUser.IsFinancier && this.loggedUser.IsCompanyAdmin);
	}

	private userCanInviteCompaniesAsSupplier(): boolean {
		if (!this.loggedUser) return false;
		if (this.loggedUser.IsSystemAdmin) return true;
		if (this.loggedUser.IsSupportOperator) return true;
		if (this.loggedUser.IsFinancier || this.loggedUser.IsSupplier) return false;
		return !!(this.loggedUser.Companies && this.loggedUser.IsCompanyAdmin);
	}

	private userCanBeInvitedAsSupplier(): boolean {
		if (!this.loggedUser) return false;
		// PR: The system user profiles (admin, supportOperator and backOfficeOperator) merely have the same access, even though only the Supplier profile represents a company invited as Supplier.
		return !!(
			this.loggedUser.IsSystemAdmin ||
			this.loggedUser.IsSupportOperator ||
			this.loggedUser?.IsBackOfficeOperator ||
			this.loggedUser.IsSupplier
		);
	}

	private userCanRequestBuyerAssociation(): boolean {
		if (!this.loggedUser) return false;
		if (this.loggedUser.IsSystemAdmin) return true;
		if (this.loggedUser.IsSupportOperator) return true;
		return !!(this.loggedUser.IsFactoringSupplier && this.loggedUser.IsCompanyAdmin);
	}

	private get activeTabCompanyUniverseSearch(): CompanyUniverseSearch {
		switch (this.activeTabId) {
			case 'myCompanies':
				return CompanyUniverseSearch.MyCompanies;
			case 'myConnections':
				return CompanyUniverseSearch.MyConnections;
			case 'myNetwork':
				return CompanyUniverseSearch.Network;
			default:
				return CompanyUniverseSearch.MyCompanies;
		}
	}
}
